import React, { useEffect, useMemo, useState } from "react";
import { ListPageLayout } from "@/components/cadastro/ListPageLayout";
import { DataTable, ColumnDef } from "@/components/cadastro/DataTable";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Pencil, Trash } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { EquipmentCache, type TipoEquipamento, type Equipamento } from "@/services/equipmentCache";

// Fonte única: EquipmentCache (tabelas `equipamento_tipos` e `equipamentos`
// no Supabase) — a MESMA origem usada pelo RDO (DialogAddEquipment).
const Equipamentos = () => {
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("equipamentos");

  const [tipos, setTipos] = useState<TipoEquipamento[]>(() => EquipmentCache.getTiposEquipamentos());
  const [equipamentos, setEquipamentos] = useState<Equipamento[]>(() => EquipmentCache.getEquipamentos());

  const refresh = () => {
    setTipos(EquipmentCache.getTiposEquipamentos());
    setEquipamentos(EquipmentCache.getEquipamentos());
  };

  useEffect(() => {
    document.title = "Cadastro - Equipamentos";
    refresh();
  }, []);

  // Modals
  const [editingEquip, setEditingEquip] = useState<Equipamento | null>(null);
  const [equipFormOpen, setEquipFormOpen] = useState(false);
  const [confirmDeleteEquip, setConfirmDeleteEquip] = useState<Equipamento | null>(null);
  const [editingTipo, setEditingTipo] = useState<TipoEquipamento | null>(null);
  const [tipoFormOpen, setTipoFormOpen] = useState(false);
  const [confirmDeleteTipo, setConfirmDeleteTipo] = useState<TipoEquipamento | null>(null);

  const filteredEquipamentos = useMemo(() => {
    const term = search.toLowerCase();
    return equipamentos.filter((e) =>
      `${e.tipo} ${e.identificacao ?? ""} ${e.fabricante ?? ""} ${e.modelo ?? ""}`.toLowerCase().includes(term)
    );
  }, [equipamentos, search]);

  const filteredTipos = useMemo(() => {
    const term = search.toLowerCase();
    return tipos.filter((t) => `${t.nome} ${t.categoria} ${t.descricao ?? ""}`.toLowerCase().includes(term));
  }, [tipos, search]);

  const isDefaultTipo = (t: TipoEquipamento) => t.id.startsWith("def-");

  // Handlers — Equipamentos
  const openNewEquip = () => {
    const now = new Date().toISOString();
    setEditingEquip({ id: "", tipo: "", identificacao: "", condicao: "Bom", ativo: true, createdAt: now, updatedAt: now });
    setEquipFormOpen(true);
  };
  const saveEquip = () => {
    if (!editingEquip) return;
    if (!editingEquip.tipo) {
      toast({ title: "Informe o tipo do equipamento", variant: "destructive" as any });
      return;
    }
    const exists = editingEquip.id && equipamentos.some((e) => e.id === editingEquip.id);
    if (exists) {
      EquipmentCache.updateEquipamento(editingEquip);
      toast({ title: "Equipamento atualizado" });
    } else {
      EquipmentCache.addEquipamento(editingEquip);
      toast({ title: "Equipamento adicionado" });
    }
    refresh();
    setEquipFormOpen(false);
    setEditingEquip(null);
  };

  // Handlers — Tipos
  const openNewTipo = () => {
    const now = new Date().toISOString();
    setEditingTipo({ id: "", nome: "", categoria: "", descricao: "", createdAt: now, updatedAt: now });
    setTipoFormOpen(true);
  };
  const saveTipo = () => {
    if (!editingTipo) return;
    if (!editingTipo.nome) {
      toast({ title: "Informe o nome do tipo", variant: "destructive" as any });
      return;
    }
    const exists = editingTipo.id && tipos.some((t) => t.id === editingTipo.id && !isDefaultTipo(t));
    if (exists) {
      EquipmentCache.updateTipoEquipamento(editingTipo);
      toast({ title: "Tipo atualizado" });
    } else {
      EquipmentCache.addTipoEquipamento(editingTipo);
      toast({ title: "Tipo adicionado" });
    }
    refresh();
    setTipoFormOpen(false);
    setEditingTipo(null);
  };

  const equipColumns: ColumnDef<Equipamento>[] = [
    { key: "tipo", header: "Tipo", sortable: true },
    { key: "identificacao", header: "Identificação", render: (v) => <span>{String(v ?? "") || "—"}</span> },
    { key: "condicao", header: "Condição", render: (v) => <span>{String(v ?? "") || "—"}</span> },
    { key: "ativo", header: "Status", render: (v) => (
      <Badge variant={v ? "default" : "secondary"}>{v ? "Ativo" : "Inativo"}</Badge>
    ) },
    { key: "id", header: "Ações", className: "w-[110px] text-right", render: (_v, row) => (
      <div className="flex justify-end gap-1">
        <Button variant="ghost" size="icon" aria-label="Editar" onClick={(e) => { e.stopPropagation(); setEditingEquip(row); setEquipFormOpen(true); }}>
          <Pencil className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" aria-label="Excluir" onClick={(e) => { e.stopPropagation(); setConfirmDeleteEquip(row); }}>
          <Trash className="h-4 w-4" />
        </Button>
      </div>
    ) },
  ];

  const tipoColumns: ColumnDef<TipoEquipamento>[] = [
    { key: "nome", header: "Nome", sortable: true },
    { key: "categoria", header: "Categoria", render: (v) => <span>{String(v ?? "") || "—"}</span> },
    { key: "descricao", header: "Descrição", render: (v) => <span>{String(v ?? "") || "—"}</span> },
    { key: "id", header: "Ações", className: "w-[110px] text-right", render: (_v, row) => (
      isDefaultTipo(row) ? (
        <Badge variant="secondary">Padrão</Badge>
      ) : (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="icon" aria-label="Editar" onClick={(e) => { e.stopPropagation(); setEditingTipo(row); setTipoFormOpen(true); }}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" aria-label="Excluir" onClick={(e) => { e.stopPropagation(); setConfirmDeleteTipo(row); }}>
            <Trash className="h-4 w-4" />
          </Button>
        </div>
      )
    ) },
  ];

  return (
    <section>
      <ListPageLayout
        title="Equipamentos"
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por tipo, identificação, fabricante…"
        onAdd={() => (activeTab === "equipamentos" ? openNewEquip() : openNewTipo())}
      >
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="equipamentos" className="h-11 sm:h-9">Equipamentos ({equipamentos.length})</TabsTrigger>
            <TabsTrigger value="tipos" className="h-11 sm:h-9">Tipos ({tipos.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="equipamentos" className="mt-4">
            {filteredEquipamentos.length === 0 ? (
              <div className="border border-dashed rounded-md p-8 text-center text-muted-foreground">
                Nenhum equipamento cadastrado.
              </div>
            ) : (
              <DataTable data={filteredEquipamentos} setData={setEquipamentos} columns={equipColumns} dense onRowClick={(row) => { setEditingEquip(row); setEquipFormOpen(true); }} />
            )}
          </TabsContent>
          <TabsContent value="tipos" className="mt-4">
            <DataTable data={filteredTipos} setData={setTipos} columns={tipoColumns} dense />
          </TabsContent>
        </Tabs>
      </ListPageLayout>

      {/* Novo/Editar Equipamento */}
      <Dialog open={equipFormOpen} onOpenChange={(o) => { setEquipFormOpen(o); if (!o) setEditingEquip(null); }}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>{editingEquip?.id ? "Editar Equipamento" : "Novo Equipamento"}</DialogTitle>
          </DialogHeader>
          {editingEquip && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Tipo*</Label>
                <Select value={editingEquip.tipo} onValueChange={(v) => setEditingEquip({ ...editingEquip, tipo: v })}>
                  <SelectTrigger><SelectValue placeholder="Selecione o tipo" /></SelectTrigger>
                  <SelectContent>
                    {tipos.map((t) => <SelectItem key={t.id} value={t.nome}>{t.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Identificação</Label>
                <Input value={editingEquip.identificacao ?? ""} onChange={(e) => setEditingEquip({ ...editingEquip, identificacao: e.target.value })} placeholder="Ex.: BT-001" />
              </div>
              <div>
                <Label>Condição</Label>
                <Select value={editingEquip.condicao || "Bom"} onValueChange={(v) => setEditingEquip({ ...editingEquip, condicao: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Bom">Bom</SelectItem>
                    <SelectItem value="Regular">Regular</SelectItem>
                    <SelectItem value="Ruim">Ruim</SelectItem>
                    <SelectItem value="Em manutenção">Em manutenção</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={editingEquip.ativo ? "ativo" : "inativo"} onValueChange={(v) => setEditingEquip({ ...editingEquip, ativo: v === "ativo" })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ativo">Ativo</SelectItem>
                    <SelectItem value="inativo">Inativo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Fabricante</Label>
                <Input value={editingEquip.fabricante ?? ""} onChange={(e) => setEditingEquip({ ...editingEquip, fabricante: e.target.value })} placeholder="Opcional" />
              </div>
              <div>
                <Label>Modelo</Label>
                <Input value={editingEquip.modelo ?? ""} onChange={(e) => setEditingEquip({ ...editingEquip, modelo: e.target.value })} placeholder="Opcional" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEquipFormOpen(false); setEditingEquip(null); }}>Cancelar</Button>
            <Button onClick={saveEquip}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Novo/Editar Tipo */}
      <Dialog open={tipoFormOpen} onOpenChange={(o) => { setTipoFormOpen(o); if (!o) setEditingTipo(null); }}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>{editingTipo?.id && !isDefaultTipo(editingTipo) ? "Editar Tipo" : "Novo Tipo de Equipamento"}</DialogTitle>
          </DialogHeader>
          {editingTipo && (
            <div className="grid gap-3">
              <div>
                <Label>Nome*</Label>
                <Input value={editingTipo.nome} onChange={(e) => setEditingTipo({ ...editingTipo, nome: e.target.value })} placeholder="Ex.: Betoneira" />
              </div>
              <div>
                <Label>Categoria</Label>
                <Input value={editingTipo.categoria ?? ""} onChange={(e) => setEditingTipo({ ...editingTipo, categoria: e.target.value })} placeholder="Ex.: Misturador" />
              </div>
              <div>
                <Label>Descrição</Label>
                <Input value={editingTipo.descricao ?? ""} onChange={(e) => setEditingTipo({ ...editingTipo, descricao: e.target.value })} placeholder="Opcional" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setTipoFormOpen(false); setEditingTipo(null); }}>Cancelar</Button>
            <Button onClick={saveTipo}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmar exclusão — Equipamento */}
      <AlertDialog open={!!confirmDeleteEquip} onOpenChange={(o) => !o && setConfirmDeleteEquip(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir equipamento?</AlertDialogTitle>
          </AlertDialogHeader>
          <p className="text-sm text-muted-foreground">Essa ação não pode ser desfeita.</p>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (confirmDeleteEquip) { EquipmentCache.deleteEquipamento(confirmDeleteEquip.id); refresh(); toast({ title: "Equipamento excluído" }); } setConfirmDeleteEquip(null); }}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmar exclusão — Tipo */}
      <AlertDialog open={!!confirmDeleteTipo} onOpenChange={(o) => !o && setConfirmDeleteTipo(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir tipo de equipamento?</AlertDialogTitle>
          </AlertDialogHeader>
          <p className="text-sm text-muted-foreground">Essa ação não pode ser desfeita.</p>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (confirmDeleteTipo) { EquipmentCache.deleteTipoEquipamento(confirmDeleteTipo.id); refresh(); toast({ title: "Tipo excluído" }); } setConfirmDeleteTipo(null); }}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};

export default Equipamentos;
